import { createFileRoute, redirect } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { OTPInput } from "@/components/common";
import { AppDispatch, store } from "@/store";
import { useDispatch } from "react-redux";
import {
  LoginCredentials,
  setCurrentUser,
  setStoreList,
  useLoginMutation,
  useSendLogInOTPMutation,
  useChangeProfileMutation,
} from "@/features/auth";
import { AsyncStorageKeyEnum } from "@/enum";
import { InfiniteCarousel } from "@/features/login/infiniteCarousel/InfiniteCarousel";
import { useGetBaseQuery } from "@/services";
import { useLazyStoreListQuery } from "@/features/business";

export const Route = createFileRoute("/(auth)/login")({
  beforeLoad: async () => {
    const user = store.getState().auth.currentUser;
    if (user) {
      // If user is logged in but has no store yet → go create one
      if (!user.name) {
        throw redirect({ to: "/store/create" });
      }
      // Otherwise go to dashboard
      throw redirect({ to: "/dashboard" });
    }
  },
  component: RouteComponent,
});

function TermsAndConditionsModal({
  isOpen,
  onClose,
  content,
}: {
  isOpen: boolean;
  onClose: () => void;
  content: string;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-contentInk/50 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surfaceBase rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-lg">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">
            Terms and Conditions
          </h2>
          <Button variant="ghost" onClick={onClose} className="h-8 w-8 p-0">
            ×
          </Button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

function RouteComponent() {
  const navigate = useNavigate();
  const [sendLoginOTP, { isLoading }] = useSendLogInOTPMutation();
  const [login, { isLoading: loginLoading }] = useLoginMutation();
  const [fetchStores] = useLazyStoreListQuery();
  const [changeProfile] = useChangeProfileMutation();
  const dispatch = useDispatch<AppDispatch>();
  const [screen, setScreen] = useState("login");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const { register, handleSubmit, setValue, watch } = useForm<LoginCredentials>(
    {
      defaultValues: {
        countryCode: "+91",
        nationalNumber: "",
      },
    }
  );
  const { data: source } = useGetBaseQuery({
    url: "v1/preference/privacy-policy",
  });

  async function submitForm(data: LoginCredentials) {
    try {
      if (screen === "otp") {
        const response = await login(data).unwrap();
        dispatch(setCurrentUser(response.data));
        localStorage.setItem(
          AsyncStorageKeyEnum.CurrentUser,
          JSON.stringify({
            ...response.data,
            profileType: "User",
          })
        );

        // Check if user has stores
        const storesResponse = await fetchStores("");
        if (!storesResponse?.data || storesResponse.data.length === 0) {
          navigate({ to: "/store/create" });
          return;
        }

        // Save store list to Redux
        dispatch(setStoreList(storesResponse.data));

        // Switch from User profile to Store profile
        // This is required for dashboard to work properly
        await changeProfile({
          storeId: storesResponse.data[0]._id,
          self: false,
        }).unwrap();

        // User has name AND store - go to dashboard
        navigate({ to: "/dashboard" });
        return;
      }
      const response = await sendLoginOTP(data).unwrap();
      if (response.data) {
        setValue("otpId", response?.data?.otpId);
        setScreen("otp");
      }
    } catch (error) {
      console.error(error);
    }
  }
  async function handelResendOTP() {
    try {
      const data = watch();
      const response = await sendLoginOTP(data).unwrap();
      if (response.data) {
        setValue("otpId", response?.data?.otpId);
        setScreen("otp");
      }
    } catch (error) {
      console.log(error);
    }
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 h-screen">
      {/* Left Side - InfiniteCarousel */}
      <div className="md:m-2 h-[60vh] md:h-auto">
        <InfiniteCarousel />
      </div>

      {/* Right Side - Login Screen */}
      <div className="flex flex-col items-center justify-center md:px-0">
        <div className="w-full md:max-w-7/12 mx-auto md:relative md:z-0 z-10 md:mt-0 -mt-24 bg-surfaceBase md:bg-transparent rounded-t-2xl md:rounded-none pt-6 md:pt-0 px-4 ">
          <div className="flex flex-col text-authBrandText">
            {/* Welcome text - hidden on mobile, visible on desktop */}
            <h1 className="hidden md:block text-3xl font-semibold text-loginHeadingText mt-4">
              Welcome!
            </h1>
            <p className="hidden md:block text-loginHeadingText text-base leading-6 px-4 sm:px-0">
              Make Your Business Smart
            </p>

            <form onSubmit={handleSubmit(submitForm)} className="w-full">
              <div className="flex items-center justify-center md:mt-4 mt:2 w-full">
                <Input
                  type="number"
                  className="max-w-[55px] pr-3 mr-2 text-right md:pr-2 h-12"
                  placeholder="+91"
                  {...register("countryCode")}
                />
                <Input
                  type="text"
                  placeholder="Mobile Number"
                  {...register("nationalNumber")}
                  autoFocus
                  className="flex-1 h-12"
                />
              </div>

              <div
                className={`min-h-[80px] transition-all duration-300 ${screen === "otp" ? "opacity-100 visible" : "opacity-0 invisible"}`}
              >
                <OTPInput
                  numberOfInputBox={6}
                  onComplete={(otp) => setValue("otp", otp)}
                  onResend={handelResendOTP}
                />
              </div>
              <div
                className={`flex items-center space-x-2 mt-8 mb-4 w-full ${screen === "login" ? "visible" : "invisible"}`}
              >
                <input
                  type="checkbox"
                  id="terms"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  className="h-4 w-4 text-themesText-600 focus:ring-themesText-500 border-gray-300 rounded"
                />
                <label htmlFor="terms" className="text-sm text-gray-700">
                  I agree to the{" "}
                  <Button
                    variant="link"
                    className="p-0 h-auto text-themesText-600 hover:text-themesText-800 font-normal"
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                  >
                    Terms and Conditions
                  </Button>
                </label>
              </div>

              <Button
                className="w-full h-[56px] rounded-full md:rounded-md"
                type="submit"
                disabled={screen === "login" && !agreeToTerms}
                onClick={handleSubmit(submitForm)}
              >
                {screen === "login" ? "Continue" : "Login"}
                {(isLoading || loginLoading) && (
                  <Loader2 className="animate-spin" />
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        content={source?.value?.description || ""}
      />
    </div>
  );
}
